import { useRef } from "react";
import { ExternalWMSConfig, WMSTileSize } from "../../../../components/sitebase/Base.types";

export default function ExternalWMSWidget(props: {
    externalWms: ExternalWMSConfig[],
    setExternalWms: (newExternalWms: ExternalWMSConfig[]) => void
}) {

    const wmsNameInput = useRef<HTMLInputElement>(null);
    const wmsUrlInput = useRef<HTMLInputElement>(null);
    const wmsFeatureInfoFormatInput = useRef<HTMLSelectElement>(null);
    const wmsVersionInput = useRef<HTMLSelectElement>(null);
    const wmsTileSizeInput = useRef<HTMLSelectElement>(null);
    const wmsGutterInput = useRef<HTMLInputElement>(null);

    return <div>
        <div className="d-flex flex-column">
            <div className='fw-bold mb-2' style={{ borderBottom: "3px solid orange" }}>Add WMS</div>
            <div className="alert alert-warning fs-8 py-2 px-3" role="alert">
                <strong>Warning:</strong> Adding external WMS layers is done at your own risk.
                Insecure or unknown WMS servers can compromise your account. External WMS URLs are
                scanned occasionally for known threats, and any URLs identified as malicious will be removed.
            </div>
            <form className="form" onSubmit={(e) => {
                e.preventDefault();

                // get the wms-url-input and break by & param
                const wmsName = wmsNameInput.current?.value;
                const wmsUrl = wmsUrlInput.current?.value;
                const wmsFeatureInfoFormat = wmsFeatureInfoFormatInput.current?.value;
                const wmsVersion = wmsVersionInput.current?.value;
                const wmsTileSize = (wmsTileSizeInput.current?.value || 'default') as WMSTileSize;
                const wmsGutter = parseInt(wmsGutterInput.current?.value || '0', 10) || 0;

                if (wmsName && wmsUrl && wmsFeatureInfoFormat && wmsVersion) {

                    // validate url and sanitise
                    let sanitisedUrl: string;
                    try {
                        const urlObj = new URL(wmsUrl);
                        urlObj.searchParams.delete("request");
                        sanitisedUrl = urlObj.toString();
                    } catch {
                        alert("The provided WMS URL is not valid.");
                        return;
                    }
                    const newEntry: ExternalWMSConfig = {
                        "url": sanitisedUrl,
                        "name": wmsName,
                        "params": "&request=GetCapabilities",
                        "version": wmsVersion,
                        "featureInfoFormat": wmsFeatureInfoFormat,
                        "capabilitiesFormat": "text/xml",
                        "latestForecastLayer": "",
                        "tileSize": wmsTileSize,
                        "gutter": wmsGutter,
                    };
                    const newExternalWms = [...props.externalWms, newEntry];
                    props.setExternalWms(newExternalWms);
                    // clear inputs
                    if (wmsNameInput.current) wmsNameInput.current.value = "";
                    if (wmsUrlInput.current) wmsUrlInput.current.value = "";
                }
            }}>
                <input ref={wmsNameInput} type="text" className="form-control" placeholder="WMS Name" id="wms-name-input" required />
                <input ref={wmsUrlInput} type="text" className="form-control" placeholder="WMS URL" id="wms-url-input" required />
                {/* select feature info format and version */}
                <div className="input-group">
                    <div className="input-group-text">Info Format</div>
                    <select ref={wmsFeatureInfoFormatInput} className="form-select">
                        <option value="text/html">text/html</option>
                        <option value="text/plain">text/plain</option>
                        <option value="application/json">application/json</option>
                    </select>
                    <div className="input-group-text">Version</div>
                    <select ref={wmsVersionInput} className="form-select">
                        <option value="1.3.0">1.3.0</option>
                        <option value="1.1.1">1.1.1</option>
                    </select>
                </div>
                <div className="input-group mt-1">
                    <div className="input-group-text">Tile Size</div>
                    <select ref={wmsTileSizeInput} className="form-select">
                        <option value="256">256×256</option>
                        <option value="512">512×512</option>
                    </select>
                    <div className="input-group-text">Gutter</div>
                    <input ref={wmsGutterInput} type="number" className="form-control" placeholder="0" defaultValue={0} min={0} max={32} style={{ maxWidth: '80px' }} />
                </div>
                <div className="d-flex flex-row justify-content-end mt-1">

                    <input type="submit" className="btn btn-warning px-4" value="Add" />
                </div>
            </form>
            <div className='fw-bold my-2 mt-4' style={{ borderBottom: "3px solid orange" }}>Loaded WMS</div>
            <div>
                {Object.entries(props.externalWms).map(([idx, wms]) => {
                    return <div key={wms.name + idx} className="d-flex flex-row justify-content-between align-items-center border p-2 mb-2">
                        <div>
                            <div><strong>{wms.name}</strong></div>
                            <div className="user-select-all overflow-x-auto fs-8">{wms.url}</div>
                            {wms.tileSize && (
                                <div className="text-muted fs-8">Tile size: {wms.tileSize}×{wms.tileSize}</div>
                            )}
                            <div className="text-muted fs-8">Gutter: {wms.gutter ?? 0}px</div>
                        </div>
                        <div>
                            <button className="btn btn-sm btn-danger" onClick={() => {
                                const newExternalWms = props.externalWms.filter((_, i) => i.toString() !== idx);
                                props.setExternalWms(newExternalWms);
                            }}>Remove</button>
                        </div>
                    </div>;
                })}
            </div>
        </div>

    </div>;
}